<template>
  <div class="mini-tab-content">
    <div
      v-if="loadingEvolutions"
      class="interc-loading"
    >
      <b-spinner
        small
        variant="info"
      />
      <span>{{ $t('involvedPanel.evolution.loading') }}</span>
    </div>
    <template v-else>
      <div class="interc-toolbar">
        <span class="interc-toolbar__title">
          <feather-icon
            icon="BookOpenIcon"
            size="13"
            class="mr-25 text-info"
          />
          {{ $tc('involvedPanel.evolution.toolbar.count', evolutions.length) }}
        </span>
        <button
          class="btn btn-sm btn-info"
          @click="showEvolutionModal = true"
        >
          <feather-icon
            icon="PlusIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.evolution.toolbar.newButton') }}
        </button>
      </div>

      <div
        v-if="!evolutions.length"
        class="interc-empty"
      >
        <feather-icon
          icon="BookOpenIcon"
          size="26"
        />
        <p>{{ $t('involvedPanel.evolution.empty') }}</p>
      </div>

      <div
        v-if="evolutions.length"
        class="interc-list"
      >
        <div
          v-for="ev in evolutions"
          :key="ev.id"
          :class="['interc-item', { 'interc-item--cancelled': ev.cancelled_by_user_id }]"
        >
          <div class="interc-item__top">
            <div class="interc-item__badge">
              <feather-icon
                :icon="ev.cancelled_by_user_id ? 'XCircleIcon' : 'BookOpenIcon'"
                size="11"
                class="mr-25"
              />
              {{ $t('involvedPanel.evolution.item.badge') }}
            </div>
            <button
              v-if="!ev.cancelled_by_user_id"
              class="interc-item__del"
              :title="$t('involvedPanel.evolution.item.deleteTooltip')"
              @click="deleteEvolution(ev.id)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="13"
              />
            </button>
          </div>

          <div
            class="evolution-content"
            v-html="ev.content"
          />

          <div
            v-if="ev.attachments && ev.attachments.length"
            class="evolution-attachments"
          >
            <template v-for="att in ev.attachments">
              <a
                v-if="att.mime_type && att.mime_type.startsWith('image/')"
                :key="att.id"
                :href="att.file_url"
                target="_blank"
                class="evolution-att-thumb"
                :title="att.file_name"
              >
                <img
                  :src="att.file_url"
                  :alt="att.file_name"
                >
              </a>
              <a
                v-else
                :key="att.id"
                :href="att.file_url"
                target="_blank"
                class="evolution-att-file"
                :title="att.file_name"
              >
                <feather-icon
                  icon="PaperclipIcon"
                  size="11"
                  class="mr-25"
                />
                {{ att.file_name }}
              </a>
            </template>
          </div>

          <div class="interc-item__meta">
            <feather-icon
              icon="UserIcon"
              size="11"
            />
            <span>{{ ev.user ? ev.user.name : '—' }}</span>
            <span class="interc-item__dot" />
            <feather-icon
              icon="ClockIcon"
              size="11"
            />
            <span>{{ ev.created_at | formatDate }}</span>
          </div>
          <div
            v-if="ev.cancelled_by_user_id"
            class="interc-item__cancelled-row"
          >
            <feather-icon
              icon="XCircleIcon"
              size="11"
              class="mr-25"
            />
            {{ $t('involvedPanel.evolution.item.cancelledBy') }}
            <strong>{{ ev.cancelled_by ? ev.cancelled_by.name : '—' }}</strong>
            · {{ ev.cancelled_at | formatDate }}
          </div>
        </div>
      </div>
    </template>

    <b-modal
      v-model="showEvolutionModal"
      :title="$t('involvedPanel.evolution.modal.title')"
      :title-class="'font-weight-bold'"
      size="lg"
      centered
      no-close-on-backdrop
      @hidden="resetEvolutionForm"
    >
      <div class="form-group mb-2">
        <label class="interc-label"><span class="required">*</span> {{ $t('involvedPanel.evolution.modal.contentLabel') }}</label>
        <ckeditor
          v-model="newEvolution.content"
          :editor="editor"
          :config="editorConfig"
        />
      </div>

      <div class="form-group mb-0">
        <label class="interc-label">{{ $t('involvedPanel.evolution.modal.attachmentsLabel') }}</label>
        <div>
          <input
            ref="evolutionFileInput"
            type="file"
            multiple
            style="display: none"
            @change="onEvolutionFiles"
          >
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="$refs.evolutionFileInput.click()"
          >
            <feather-icon
              icon="PaperclipIcon"
              size="13"
              class="mr-25"
            />
            {{ $t('involvedPanel.evolution.modal.attachButton') }}
          </button>
        </div>
        <div
          v-if="evolutionFiles.length"
          class="evolution-file-picklist mt-1"
        >
          <div
            v-for="(f, idx) in evolutionFiles"
            :key="idx"
            class="evolution-file-pick"
          >
            <feather-icon
              icon="FileIcon"
              size="12"
              class="mr-25"
            />
            <span class="evolution-file-pick__name">{{ f.name }}</span>
            <button
              type="button"
              class="btn btn-link p-0 text-danger ml-50"
              style="font-size: 11px; line-height: 1"
              :title="$t('involvedPanel.evolution.modal.removeTooltip')"
              @click="removeEvolutionFile(idx)"
            >
              <feather-icon
                icon="XIcon"
                size="12"
              />
            </button>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="showEvolutionModal = false"
        >
          {{ $t('involvedPanel.evolution.modal.cancelButton') }}
        </button>
        <button
          class="btn btn-sm btn-info"
          :disabled="savingEvolution || !evolutionHasContent"
          @click="saveEvolution"
        >
          <b-spinner
            v-if="savingEvolution"
            small
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="SaveIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.evolution.modal.submitButton') }}
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue2'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import involvedPanelMixin from './involvedPanelMixin'

export default {
  name: 'EvolutionPanel',
  components: { ckeditor: CKEditor.component },
  mixins: [involvedPanelMixin],
  data() {
    return {
      evolutions: [],
      loadingEvolutions: false,
      showEvolutionModal: false,
      savingEvolution: false,
      newEvolution: { content: '' },
      evolutionFiles: [],
      editor: ClassicEditor,
      editorConfig: {
        toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', '|', 'undo', 'redo'],
      },
    }
  },
  computed: {
    evolutionHasContent() {
      return !!(this.newEvolution.content && this.newEvolution.content.trim())
    },
  },
  mounted() {
    this.loadEvolutions()
  },
  methods: {
    async loadEvolutions() {
      this.loadingEvolutions = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrence_evolutions`, {
          params: {
            occurrence_id: Number(this.occurrenceId),
            involved_id: this.involvedId ? Number(this.involvedId) : undefined,
          },
        })
        this.evolutions = res.data.data || []
      } catch (e) {
        this.evolutions = []
        this.$message('Erro', this.$t('involvedPanel.evolution.messages.loadError'), 'error')
      } finally {
        this.loadingEvolutions = false
      }
    },
    onEvolutionFiles(e) {
      const files = Array.from(e.target.files || [])
      this.evolutionFiles.push(...files)
      e.target.value = ''
    },
    removeEvolutionFile(idx) {
      this.evolutionFiles.splice(idx, 1)
    },
    resetEvolutionForm() {
      this.newEvolution = { content: '' }
      this.evolutionFiles = []
    },
    async saveEvolution() {
      if (!this.evolutionHasContent) return
      this.savingEvolution = true
      try {
        const { user } = this.$store.state
        const fd = new FormData()
        fd.append('occurrence_id', Number(this.occurrenceId))
        if (this.involvedId) fd.append('involved_id', Number(this.involvedId))
        fd.append('user_id', user.id)
        fd.append('content', this.newEvolution.content)
        this.evolutionFiles.forEach(f => fd.append('attachments[]', f))

        const res = await this.$http.post(`${this.$store.state.api}occurrence_evolutions`, fd)
        const saved = res.data.data || res.data
        this.evolutions.push(saved)
        this.resetEvolutionForm()
        this.showEvolutionModal = false
        this.$message(this.$t('involvedPanel.evolution.messages.saveTitle'), this.$t('involvedPanel.evolution.messages.saveSuccess'), 'success')
      } catch (e) {
        this.$message('Erro', this.$t('involvedPanel.evolution.messages.saveError'), 'error')
      } finally {
        this.savingEvolution = false
      }
    },
    async deleteEvolution(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('involvedPanel.evolution.messages.deleteConfirm'),
        {
          title: this.$t('involvedPanel.evolution.messages.deleteTitle'), okTitle: this.$t('involvedPanel.evolution.messages.deleteOkButton'), cancelTitle: this.$t('involvedPanel.evolution.messages.deleteCancelButton'), okVariant: 'danger', centered: true, size: 'sm',
        },
      )
      if (!confirmed) return
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_evolutions/${id}`)
        const updated = res.data.data || res.data
        const idx = this.evolutions.findIndex(ev => ev.id === id)
        if (idx !== -1) this.$set(this.evolutions, idx, updated)
      } catch (e) {
        this.$message('Erro', this.$t('involvedPanel.evolution.messages.deleteError'), 'error')
      }
    },
  },
}
</script>
